import { QueryCache, QueryClient, type Query } from "@tanstack/react-query";
import { ApiError } from "api/apiError";
import { reporterForStatus } from "api/reporterForStatus";

/**
 * Reports a failed query to Sentry, like the Apollo error link does for
 * GraphQL. TanStack Query calls this once, after the last retry. The method
 * and the path template (the start of a `useRestQuery` key) name the query:
 * the URL contains ids, so it cannot group the issues.
 */
const reportQueryError = (
  error: Error,
  { queryKey }: Pick<Query, "queryKey">,
) => {
  const status = error instanceof ApiError ? error.status : undefined;
  const code = error instanceof ApiError ? error.code : undefined;
  const queryName = queryKey.slice(0, 2).map(String).join(" ");
  const message = `[Query]: (${status}) Query: ${queryName} | Message: ${error.message}`;

  console.error(message);
  reporterForStatus(status)(message, {
    // A status means that the server answered. No status means that the
    // request did not complete (offline, CORS, timeout).
    source: status === undefined ? "network" : "client",
    statusCode: status,
    // The REST spec names an owner for each operation (`x-tigerdata-owner`),
    // but the client does not have it yet. Use the default of the Apollo
    // error link.
    team: "backend",
    ...(code ? { code } : {}),
    queryKey,
    // Send the original error when the request did not complete, so that
    // Sentry keeps its type and stack, and drops benign fetch failures.
    ...(status === undefined ? { error } : {}),
    errorName: error.name,
    online: navigator.onLine,
    fingerprint: ["query-error", queryName, String(status ?? "unknown")],
  });
};

/** The cache of the server data that TanStack Query loads. */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reportQueryError }),
  defaultOptions: {
    queries: {
      // Like the Apollo hooks, do not refetch on every window focus.
      refetchOnWindowFocus: false,
      // Keep unused data for 60 minutes, so that a page that opens again shows
      // its data at once while it refetches. The default is 5 minutes.
      gcTime: 60 * 60 * 1000,
      // A client error does not go away on retry.
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status < 500) && failureCount < 2,
    },
  },
});
